import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './Stations.css'

const EMPTY_CREATE = {
  station_id:          '',
  name:                '',
  owner_tenant_id:     '',
  sip_2n_ip:           '',
  sip_2n_api_user:     '',
  sip_2n_api_password: '',
  evac_pin:            '',
}

const EMPTY_BARRIER = {
  label:                        '',
  barrier_ip:                   '',
  barrier_port:                 '502',
  barrier_unit_id:              '1',
  barrier_coil_address:         '0',
  barrier_close_coil_address:  '',
  barrier_auto_close_seconds:   '',
  barrier_auto_close_seconds_standby: '',
  barrier_trigger_delay_seconds: '0',
  camera_trigger_direction:    '',
  camera_trigger_standby_only: false,
  camera_trigger_delay_seconds_standby: '',
  barrier_signal_mode:         'pulse',
  // UI-only — not sent directly to the server; saveBarrier translates this into
  // barrier_business_hours / barrier_hours_reference_label (the two are mutually
  // exclusive, see validateBarrierBody on the server).
  barrier_hours_source:        'tenant', // 'tenant' | 'custom' | 'reference'
  barrier_business_hours:      null,
  barrier_hours_reference_label: '',
  camera_trigger_stays_open_after_midnight: false,
  // Migration 036 — dashboard control. The server address is the terminal PC's
  // WireGuard IP + forwarded port, NOT the ADAM's own address.
  server_barrier_ip:            '',
  server_barrier_port:          '',
  dashboard_control_enabled:    false,
  barrier_stop_coil_address:    '',
  lock_open_until_closed:       false,
  open_reminder_after_minutes:  '',
  open_reminder_at_closing:     false,
  auto_close_at:                '',
  open_input_address:           '',
  closed_input_address:         '',
}

// String form-field value for a nullable number from the server.
const numField = (v) => (v != null ? String(v) : '')
// Nullable number for the server from a string form field.
const numOrNull = (v) => (String(v ?? '').trim() ? Number(v) : null)

// A barrier stays open after its Auf until something closes it — only then can it be
// "forgotten open", and only then do the Auf-lock and the reminders mean anything.
const formStaysOpen = (form) =>
  !!String(form.barrier_close_coil_address ?? '').trim() || form.barrier_signal_mode === 'continuous'

/**
 * Manuell / Gemischt / Automatisch — derived from what is configured, never stored, so
 * it cannot contradict the real setup. Automation = some flow opens it at check-in, or
 * a camera direction is set.
 */
function barrierMode(b) {
  const automatic = (b.flow_usage || []).length > 0 || !!b.camera_trigger_direction
  const manual = !!b.dashboard_control_enabled
  if (manual && automatic) return { label: 'Gemischt',    className: 'badge badge-blue' }
  if (manual)              return { label: 'Manuell',     className: 'badge badge-orange' }
  if (automatic)           return { label: 'Automatisch', className: 'badge badge-green' }
  return { label: 'Nicht verwendet', className: 'badge badge-grey' }
}

const WEEKDAYS = [
  ['mon', 'Mo'], ['tue', 'Di'], ['wed', 'Mi'], ['thu', 'Do'],
  ['fri', 'Fr'], ['sat', 'Sa'], ['sun', 'So'],
]
const DEFAULT_CUSTOM_HOURS = Object.fromEntries(WEEKDAYS.map(([key]) => [key, { start: '06:00', end: '15:30' }]))

// Shared field grid for both the "add barrier" and "edit barrier" inline
// forms — each barrier is its own independent Add/Edit/Delete now (not a
// bundled list saved all at once), so this renders once per open form.
// siblingLabels: the OTHER barriers already saved at this station (excludes the one
// currently being edited, if any) — populates the "reference another barrier" dropdown.
const BarrierFormFields = ({ form, onChange, siblingLabels = [] }) => (
  <div className="station-form-grid">
    <div className="form-group">
      <label>Name</label>
      <input className="form-control" placeholder="z. B. Einfahrt"
        value={form.label}
        onChange={e => onChange('label', e.target.value)} />
      <span className="form-hint">Unter diesem Namen wird die Schranke im Flow Builder ausgewählt.</span>
    </div>
    <div className="form-group">
      <label>Adresse im Netzwerk (Terminal) — IP</label>
      <input className="form-control" placeholder="192.168.1.60"
        value={form.barrier_ip}
        onChange={e => onChange('barrier_ip', e.target.value)} />
      <span className="form-hint">Die IP des ADAM-Moduls im Netzwerk des Kunden. Damit öffnet das Terminal die Schranke beim Check-in. Leer lassen, wenn die Schranke nur vom Server gesteuert wird.</span>
    </div>
    <div className="form-group">
      <label>Adresse im Netzwerk (Terminal) — Port</label>
      <input className="form-control" placeholder="502"
        value={form.barrier_port}
        onChange={e => onChange('barrier_port', e.target.value)} />
    </div>
    <div className="form-group">
      <label>Adresse über WireGuard (Server) — IP</label>
      <input className="form-control" placeholder="z. B. 10.100.7.2"
        value={form.server_barrier_ip}
        onChange={e => onChange('server_barrier_ip', e.target.value)} />
      <span className="form-hint">Nicht die IP des ADAM-Moduls, sondern die Tunnel-Adresse des Terminal-PCs (bzw. Routers). Eine Weiterleitung dort leitet den Port an das ADAM-Modul weiter. Nötig für Dashboard und Kamera. Leer = der Server verwendet die Adresse oben (bisheriges Verhalten).</span>
    </div>
    <div className="form-group">
      <label>Adresse über WireGuard (Server) — Port</label>
      <input className="form-control" placeholder="z. B. 8502"
        value={form.server_barrier_port}
        onChange={e => onChange('server_barrier_port', e.target.value)} />
      <span className="form-hint">Der weitergeleitete Port auf dem Terminal-PC — ein eigener Port pro ADAM-Modul.</span>
    </div>
    <div className="form-group">
      <label>Unit-ID</label>
      <input className="form-control" placeholder="1"
        value={form.barrier_unit_id}
        onChange={e => onChange('barrier_unit_id', e.target.value)} />
    </div>
    <div className="form-group">
      <label>Relais-Adresse (Coil) — Öffnen</label>
      <input className="form-control" placeholder="0"
        value={form.barrier_coil_address}
        onChange={e => onChange('barrier_coil_address', e.target.value)} />
    </div>
    <div className="form-group">
      <label>Relais-Adresse (Coil) — Schließen (optional)</label>
      <input className="form-control" placeholder="leer = dasselbe Relais wie Öffnen"
        value={form.barrier_close_coil_address}
        onChange={e => onChange('barrier_close_coil_address', e.target.value)} />
      <span className="form-hint">Nur ausfüllen, wenn Öffnen und Schließen zwei getrennte Impuls-Eingänge sind (z. B. Rolltor mit "Auf"/"Zu"). Leer lassen für die übliche Schranke mit nur einem Relais.</span>
    </div>
    <div className="form-group">
      <label>Relais-Adresse (Coil) — Stopp (optional)</label>
      <input className="form-control" placeholder="leer = kein Stopp-Eingang"
        value={form.barrier_stop_coil_address}
        onChange={e => onChange('barrier_stop_coil_address', e.target.value)} />
      <span className="form-hint">Nur bei Toren mit eigenem Stopp-Eingang. Dann erscheint im Dashboard zusätzlich „Stopp".</span>
    </div>
    <div className="form-group">
      <label>Auto-Schließen nach (Sekunden)</label>
      <input className="form-control" placeholder="leer = Schranke schließt selbstständig"
        value={form.barrier_auto_close_seconds}
        onChange={e => onChange('barrier_auto_close_seconds', e.target.value)} />
      <span className="form-hint">Leer lassen, wenn die Schranke einen eigenen Antrieb hat (empfohlen). Bei zwei getrennten Relais (oben) ist dies die Wartezeit zwischen Öffnen- und Schließen-Impuls, nicht eine Haltezeit.</span>
    </div>
    <div className="form-group">
      <label>Auto-Schließen nach (Sekunden) — außerhalb der Betriebszeiten</label>
      <input className="form-control" placeholder="leer = wie oben, keine Sonderregel"
        value={form.barrier_auto_close_seconds_standby}
        onChange={e => onChange('barrier_auto_close_seconds_standby', e.target.value)} />
      <span className="form-hint">Überschreibt den obigen Wert nur, wenn der Kamera-Treffer während der Standby-Zeit (geschlossen) passiert — z. B. weil ein langsameres Rolltor auf derselben Einfahrt länger zum Öffnen braucht als die normale Durchfahrtszeit tagsüber.</span>
    </div>
    <div className="form-group">
      <label>Verzögerung vor dem Öffnen (Sekunden)</label>
      <input className="form-control" placeholder="0"
        value={form.barrier_trigger_delay_seconds}
        onChange={e => onChange('barrier_trigger_delay_seconds', e.target.value)} />
      <span className="form-hint">Wartezeit nach erfolgreichem Check-in, bevor das Signal gesendet wird — z. B. damit der Besucher Zeit hat, zu seinem Fahrzeug zurückzugehen.</span>
    </div>
    <div className="form-group">
      <label>Kamera-Auslöserichtung</label>
      <select className="form-control"
        value={form.camera_trigger_direction}
        onChange={e => onChange('camera_trigger_direction', e.target.value)}>
        <option value="">Keine (nicht kameragesteuert)</option>
        <option value="in">Einfahrt</option>
        <option value="out">Ausfahrt</option>
        <option value="any">Alle Richtungen (nur zum Testen)</option>
      </select>
      <span className="form-hint">Löst diese Schranke aus, wenn die AXIS-Kamera ein zugelassenes Kennzeichen in dieser Richtung erkennt — unabhängig vom Namen oben und getrennt vom Kiosk-Check-in-Auslöser. "Alle Richtungen" ignoriert die erkannte Richtung komplett (auch "unbekannt") und ist nur für Tests gedacht.</span>
    </div>
    <div className="form-group form-group-checkbox">
      <label>
        <input type="checkbox"
          checked={form.camera_trigger_standby_only}
          onChange={e => onChange('camera_trigger_standby_only', e.target.checked)} />
        {' '}Nur außerhalb der Betriebszeiten auslösen
      </label>
      <span className="form-hint">Für Schranken/Tore, deren Öffnen/Schließen während der Betriebszeiten automatisch (ohne viGuard) geschieht — ein Kamera-Treffer öffnet dann nur außerhalb der Betriebszeiten (Standby). Während der Betriebszeiten wird ein Treffer ignoriert.</span>
    </div>
    <div className="form-group">
      <label>Verzögerung vor dem Öffnen (Sekunden) — außerhalb der Betriebszeiten</label>
      <input className="form-control" placeholder="leer = öffnet sofort, wie tagsüber"
        value={form.camera_trigger_delay_seconds_standby}
        onChange={e => onChange('camera_trigger_delay_seconds_standby', e.target.value)} />
      <span className="form-hint">Unabhängig von der Checkbox oben: Diese Schranke öffnet bei einem Kamera-Treffer tagsüber weiterhin sofort, aber außerhalb der Betriebszeiten erst nach dieser Wartezeit — z. B. damit sie erst öffnet, wenn ein langsameres Rolltor auf derselben Einfahrt bereits einen Vorsprung beim Öffnen hatte, statt beide gleichzeitig zu starten.</span>
    </div>
    <div className="form-group">
      <label>Zeitfenster-Quelle</label>
      <select className="form-control"
        value={form.barrier_hours_source}
        onChange={e => {
          const next = e.target.value
          onChange('barrier_hours_source', next)
          if (next === 'custom' && !form.barrier_business_hours) onChange('barrier_business_hours', DEFAULT_CUSTOM_HOURS)
        }}>
        <option value="tenant">Mandanten-Standardzeiten</option>
        <option value="custom">Eigene Zeiten festlegen</option>
        <option value="reference">Zeiten einer anderen Schranke übernehmen</option>
      </select>
      <span className="form-hint">Bestimmt, welche Betriebszeiten für die obigen Standby-Felder gelten (Verzögerung, Auto-Schließen außerhalb der Betriebszeiten, "nur außerhalb der Betriebszeiten auslösen").</span>
    </div>
    {form.barrier_hours_source === 'custom' && (
      <div className="form-group station-hours-group">
        <label>Eigene Betriebszeiten</label>
        <div className="station-hours-grid">
          {WEEKDAYS.map(([key, dayLabel]) => {
            const day = form.barrier_business_hours?.[key] || { start: '', end: '' }
            const setDay = (next) => onChange('barrier_business_hours', { ...form.barrier_business_hours, [key]: next })
            return (
              <div key={key} className="station-hours-row">
                <span className="station-hours-day">{dayLabel}</span>
                <input type="time" className="form-control"
                  value={day.start}
                  onChange={e => setDay({ ...day, start: e.target.value })} />
                <span>–</span>
                <input type="time" className="form-control"
                  value={day.end}
                  onChange={e => setDay({ ...day, end: e.target.value })} />
                {/* Lunch break — the barrier treats it like closed hours (standby). */}
                <label className="station-hours-break">
                  <input type="checkbox" checked={!!day.break}
                    onChange={e => setDay({ ...day, break: e.target.checked ? { start: '12:00', end: '13:00' } : undefined })} />
                  {' '}Pause
                </label>
                {day.break && (
                  <>
                    <input type="time" className="form-control"
                      value={day.break.start}
                      onChange={e => setDay({ ...day, break: { ...day.break, start: e.target.value } })} />
                    <span>–</span>
                    <input type="time" className="form-control"
                      value={day.break.end}
                      onChange={e => setDay({ ...day, break: { ...day.break, end: e.target.value } })} />
                  </>
                )}
              </div>
            )
          })}
        </div>
        <span className="form-hint">Gilt nur für diese Schranke — unabhängig von den mandantenweiten Standby-Zeiten im Terminal. Beide Zeiten leeren = an diesem Tag geschlossen.</span>
        <label className="station-hours-holidays">
          <input type="checkbox"
            checked={form.barrier_business_hours?.followHolidays !== false}
            onChange={e => onChange('barrier_business_hours', { ...form.barrier_business_hours, followHolidays: e.target.checked })} />
          {' '}Feiertage des Mandanten beachten
        </label>
        <span className="form-hint">An einem Feiertag (Standby-Bildschirm → Feiertage) gelten dessen Zeiten auch für diese Schranke: geschlossen den ganzen Tag oder die Sonderöffnungszeiten. Kamera-Treffer öffnen sie weiterhin.</span>
      </div>
    )}
    {form.barrier_hours_source === 'reference' && (
      <div className="form-group">
        <label>Zeiten übernehmen von</label>
        <select className="form-control"
          value={form.barrier_hours_reference_label}
          onChange={e => onChange('barrier_hours_reference_label', e.target.value)}>
          <option value="">— auswählen —</option>
          {siblingLabels.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <span className="form-hint">Diese Schranke verwendet dieselben Betriebszeiten wie die ausgewählte — ändert sich deren Zeitplan, folgt diese automatisch, ohne hier erneut gepflegt werden zu müssen.</span>
      </div>
    )}
    <div className="form-group form-group-checkbox">
      <label>
        <input type="checkbox"
          checked={form.camera_trigger_stays_open_after_midnight}
          onChange={e => onChange('camera_trigger_stays_open_after_midnight', e.target.checked)} />
        {' '}Nach Mitternacht beim ersten Fahrzeug dauerhaft öffnen
      </label>
      <span className="form-hint">Nur relevant zwischen Mitternacht und Betriebsbeginn: Das erste zugelassene Fahrzeug öffnet diese Schranke, danach bleibt sie offen (kein Schließen-Impuls) bis Betriebsbeginn — weitere Fahrzeuge in dieser Zeit werden ignoriert, da bereits offen. Am Abend (nach Betriebsende, vor Mitternacht) bleibt das normale Impuls-Verhalten pro Fahrzeug unverändert.</span>
    </div>
    <div className="form-group">
      <label>Signalmodus</label>
      <select className="form-control"
        value={form.barrier_signal_mode}
        onChange={e => onChange('barrier_signal_mode', e.target.value)}>
        <option value="pulse">Impuls (Standard)</option>
        <option value="continuous">Dauerkontakt</option>
      </select>
      <span className="form-hint">Impuls: Relais wird kurz ein- und wieder ausgeschaltet. Dauerkontakt: Relais bleibt dauerhaft geschlossen, solange laut Standby-Zeitfenster geöffnet sein soll, bis ein separates Lösen erfolgt — nur für Antriebe mit einem echten Dauersignal-Eingang (kein Impuls-Eingang wie oben).</span>
    </div>

    {/* ── Dashboard control (migration 036) ── */}
    <div className="station-form-subheader">Steuerung im Dashboard</div>
    <div className="form-group form-group-checkbox">
      <label>
        <input type="checkbox"
          checked={form.dashboard_control_enabled}
          disabled={!String(form.server_barrier_ip ?? '').trim()}
          onChange={e => onChange('dashboard_control_enabled', e.target.checked)} />
        {' '}Im Dashboard steuerbar
      </label>
      {!String(form.server_barrier_ip ?? '').trim() && (
        <span className="form-hint station-hint-blocked">
          Erst die „Adresse über WireGuard (Server)" oben eintragen — darüber schaltet das Dashboard.
        </span>
      )}
      <span className="form-hint">
        Zeigt in der Disposition Schaltflächen für diese Schranke — nur „Auf" bei einer Schranke mit einem
        Relais, „Auf"/„Zu" bei getrennten Relais, dazu „Stopp", wenn ein Stopp-Relais eingetragen ist.
        Bedienen kann sie jeder Benutzer des Mandanten, dem diese Station gehört. Benötigt die Adresse über
        WireGuard und die Funktion „Schranken im Dashboard steuern" beim Mandanten.
      </span>
    </div>

    {form.dashboard_control_enabled && formStaysOpen(form) && (
      <>
        <div className="form-group form-group-checkbox">
          <label>
            <input type="checkbox"
              checked={form.lock_open_until_closed}
              onChange={e => onChange('lock_open_until_closed', e.target.checked)} />
            {' '}„Auf" sperren, bis „Zu" gedrückt wurde
          </label>
          <span className="form-hint">Verhindert doppeltes Öffnen. Grundlage ist der letzte gesendete Befehl, nicht die tatsächliche Stellung — wurde die Schranke von Hand geschlossen, bleibt „Trotzdem öffnen" möglich (wird protokolliert).</span>
        </div>
        <div className="form-group">
          <label>Hinweis, wenn länger offen als (Minuten)</label>
          <input className="form-control" placeholder="leer = kein Hinweis"
            value={form.open_reminder_after_minutes}
            onChange={e => onChange('open_reminder_after_minutes', e.target.value)} />
          <span className="form-hint">Meldung im Dashboard, einmal pro Öffnung.</span>
        </div>
        <div className="form-group form-group-checkbox">
          <label>
            <input type="checkbox"
              checked={form.open_reminder_at_closing}
              onChange={e => onChange('open_reminder_at_closing', e.target.checked)} />
            {' '}Hinweis bei Betriebsende, wenn noch offen
          </label>
          <span className="form-hint">Meldung zum Ende der Betriebszeiten, wenn die Schranke zu diesem Zeitpunkt noch offen ist — unabhängig von „Automatisch schließen um". Das Betriebsende kommt aus der Zeitfenster-Quelle oben (eigene Zeiten, Zeiten einer anderen Schranke oder die Standby-Zeiten des Mandanten). Ohne hinterlegte Zeiten gibt es kein Betriebsende — dann erscheint kein Hinweis.</span>
        </div>
        <div className="form-group">
          <label>Automatisch schließen um</label>
          <input type="time" className="form-control"
            value={form.auto_close_at}
            onChange={e => onChange('auto_close_at', e.target.value)} />
          <span className="form-hint">Sendet täglich zu dieser Uhrzeit „Zu" — ohne Kamerabild. Nur einstellen, wenn die Anlage beim Schließen selbst auf Hindernisse reagiert (Lichtschranke oder Schließkantensicherung). Eine Meldung erscheint nur, wenn das Schließen fehlschlägt. Leer = nie.</span>
        </div>
      </>
    )}
    {form.dashboard_control_enabled && !formStaysOpen(form) && (
      <span className="form-hint station-form-fullwidth">
        Diese Schranke schließt selbstständig. Sperre, Hinweise und automatisches Schließen gibt es nur für
        Schranken mit getrenntem „Zu"-Relais oder im Dauerkontakt-Modus.
      </span>
    )}

    <div className="station-form-subheader">Endlagenschalter (vorbereitet, optional)</div>
    <div className="form-group">
      <label>Eingang „offen" (DI-Adresse)</label>
      <input className="form-control" placeholder="leer = nicht angeschlossen"
        value={form.open_input_address}
        onChange={e => onChange('open_input_address', e.target.value)} />
    </div>
    <div className="form-group">
      <label>Eingang „geschlossen" (DI-Adresse)</label>
      <input className="form-control" placeholder="leer = nicht angeschlossen"
        value={form.closed_input_address}
        onChange={e => onChange('closed_input_address', e.target.value)} />
      <span className="form-hint">Nur eintragen, wenn Endlagenschalter an digitale Eingänge des ADAM-Moduls angeschlossen sind. Wird derzeit noch nicht ausgewertet.</span>
    </div>
  </div>
)

const Stations = () => {
  const { apiFetch } = useAuth()

  const [stations,   setStations]   = useState([])
  const [allTenants, setAllTenants] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)
  const [success,    setSuccess]    = useState(null)

  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState(EMPTY_CREATE)
  const [creating,   setCreating]   = useState(false)

  const [editingId,    setEditingId]    = useState(null)
  const [editBasic,    setEditBasic]    = useState({})
  const [editTenants,  setEditTenants]  = useState([])
  const [addTenantId, setAddTenantId] = useState('')
  const [saving,      setSaving]      = useState(false)
  const [test2nState, setTest2nState] = useState(null)

  // Barriers are saved independently now (their own Add/Edit/Delete calls),
  // not bundled into the station's editBasic/editTenants Save button — see
  // barrierEditingId: null = nothing open, 'new' = the add-barrier form is
  // open, or a barrier's own id = that barrier's edit form is open.
  const [barrierEditingId, setBarrierEditingId] = useState(null)
  const [barrierForm,      setBarrierForm]      = useState({})
  const [barrierSaving,    setBarrierSaving]    = useState(false)
  // "Verbindung testen" results, per barrier id: 'running' | { ok, relayOn, error, ms }
  const [barrierTests,     setBarrierTests]     = useState({})

  const testBarrier = async (stationId, barrierId) => {
    setBarrierTests(prev => ({ ...prev, [barrierId]: 'running' }))
    try {
      const res = await apiFetch(`/api/admin/stations/${stationId}/barriers/${barrierId}/test`, { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      setBarrierTests(prev => ({ ...prev, [barrierId]: res.ok ? data : { ok: false, error: data.error || 'Test fehlgeschlagen' } }))
    } catch {
      setBarrierTests(prev => ({ ...prev, [barrierId]: { ok: false, error: 'Server nicht erreichbar' } }))
    }
  }

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(null), 3000) }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [sRes, tRes] = await Promise.all([
        apiFetch('/api/admin/stations'),
        apiFetch('/api/admin/tenants'),
      ])
      const [sData, tData] = await Promise.all([sRes.json(), tRes.json()])
      setStations(Array.isArray(sData) ? sData : [])
      setAllTenants(Array.isArray(tData) ? tData : [])
    } catch {
      setError('Laden fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }, [apiFetch])

  useEffect(() => { load() }, [load])

  // ── Create ───────────────────────────────────────────────────────────────────

  const handleCreate = async () => {
    setError(null)
    if (!createForm.station_id.trim() || !createForm.name.trim()) {
      setError('Station-ID und Name sind Pflichtfelder')
      return
    }
    setCreating(true)
    try {
      const res = await apiFetch('/api/admin/stations', {
        method: 'POST',
        body: JSON.stringify({
          station_id:      createForm.station_id.trim(),
          name:            createForm.name.trim(),
          owner_tenant_id: createForm.owner_tenant_id     || null,
          sip_2n_ip:       createForm.sip_2n_ip.trim()   || null,
          sip_2n_api_user: createForm.sip_2n_api_user.trim() || null,
          ...(createForm.sip_2n_api_password.trim() && { sip_2n_api_password: createForm.sip_2n_api_password.trim() }),
          ...(createForm.evac_pin.trim() && { evac_pin: createForm.evac_pin.trim() }),
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        setError(err.error || 'Erstellen fehlgeschlagen')
        return
      }
      const created = await res.json()
      setStations(prev => [{ ...created, station_tenants: [], station_barriers: [] }, ...prev])
      setCreateForm(EMPTY_CREATE)
      setShowCreate(false)
      flash('Station erstellt')
    } catch { setError('Erstellen fehlgeschlagen') }
    finally  { setCreating(false) }
  }

  // ── Edit ─────────────────────────────────────────────────────────────────────

  const startEdit = (s) => {
    setEditingId(s.id)
    setEditBasic({
      name:                s.name,
      owner_tenant_id:     s.owner_tenant_id     || '',
      sip_2n_ip:           s.sip_2n_ip           || '',
      sip_2n_api_user:     s.sip_2n_api_user     || '',
      sip_2n_api_password: '',
      evac_pin:            '',
    })
    setEditTenants((s.station_tenants || []).map(st => ({
      tenant_id:          st.tenant_id,
      sort_order:         st.sort_order,
      allowed_flow_types: st.allowed_flow_types || null,
      default_flow_type:  st.default_flow_type  || null,
      _tenant:            st.tenants,
    })))
    setAddTenantId('')
    setTest2nState(null)
    setBarrierEditingId(null)
    setBarrierForm({})
    setError(null)
  }

  const cancelEdit = () => {
    setEditingId(null); setEditBasic({}); setEditTenants([])
    setBarrierEditingId(null); setBarrierForm({})
  }

  const saveEdit = async (stationId) => {
    setError(null)
    if (!editBasic.name.trim()) { setError('Name ist Pflichtfeld'); return }
    setSaving(true)
    try {
      const [basicRes, tenantsRes] = await Promise.all([
        apiFetch(`/api/admin/stations/${stationId}`, {
          method: 'PUT',
          body: JSON.stringify({
            name:            editBasic.name.trim(),
            owner_tenant_id: editBasic.owner_tenant_id || null,
            sip_2n_ip:       editBasic.sip_2n_ip.trim()       || null,
            sip_2n_api_user: editBasic.sip_2n_api_user.trim() || null,
            ...(editBasic.sip_2n_api_password.trim() && { sip_2n_api_password: editBasic.sip_2n_api_password.trim() }),
            ...(editBasic.evac_pin.trim() && { evac_pin: editBasic.evac_pin.trim() }),
          }),
        }),
        apiFetch(`/api/admin/stations/${stationId}/tenants`, {
          method: 'PUT',
          body: JSON.stringify(editTenants.map(({ _tenant, ...t }) => t)),
        }),
      ])

      if (!basicRes.ok) {
        const err = await basicRes.json().catch(() => ({}))
        setError(err.error || 'Speichern fehlgeschlagen')
        return
      }
      if (!tenantsRes.ok) {
        const err = await tenantsRes.json().catch(() => ({}))
        setError(err.error || 'Mandantenzuweisung fehlgeschlagen')
        return
      }

      const updated = await basicRes.json()
      setStations(prev => prev.map(s => s.id === stationId
        ? {
            ...updated,
            station_tenants: editTenants.map(({ _tenant, ...t }) => ({ ...t, tenants: _tenant })),
            station_barriers: s.station_barriers,
          }
        : s
      ))
      setEditingId(null)
      flash('Gespeichert')
    } catch { setError('Speichern fehlgeschlagen') }
    finally  { setSaving(false) }
  }

  // ── Barrier CRUD — each barrier is saved independently (own Add/Edit/
  // Delete request), not bundled into saveEdit above, so its id stays
  // stable across edits (needed for versioning/restore to work correctly). ──

  const startBarrierEdit = (b) => {
    setBarrierEditingId(b.id)
    setBarrierForm({
      label:                        b.label,
      barrier_ip:                   b.barrier_ip || '', // null for a server-only barrier
      barrier_port:                 String(b.barrier_port ?? 502),
      barrier_unit_id:              String(b.barrier_unit_id ?? 1),
      barrier_coil_address:         String(b.barrier_coil_address ?? 0),
      barrier_close_coil_address:  b.barrier_close_coil_address != null ? String(b.barrier_close_coil_address) : '',
      barrier_auto_close_seconds:   b.barrier_auto_close_seconds != null ? String(b.barrier_auto_close_seconds) : '',
      barrier_auto_close_seconds_standby: b.barrier_auto_close_seconds_standby != null ? String(b.barrier_auto_close_seconds_standby) : '',
      barrier_trigger_delay_seconds: String(b.barrier_trigger_delay_seconds ?? 0),
      camera_trigger_direction:    b.camera_trigger_direction || '',
      camera_trigger_standby_only: !!b.camera_trigger_standby_only,
      camera_trigger_delay_seconds_standby: b.camera_trigger_delay_seconds_standby != null ? String(b.camera_trigger_delay_seconds_standby) : '',
      barrier_signal_mode:         b.barrier_signal_mode || 'pulse',
      barrier_hours_source:        b.barrier_hours_reference_label ? 'reference' : (b.barrier_business_hours ? 'custom' : 'tenant'),
      barrier_business_hours:      b.barrier_business_hours || null,
      barrier_hours_reference_label: b.barrier_hours_reference_label || '',
      camera_trigger_stays_open_after_midnight: !!b.camera_trigger_stays_open_after_midnight,
      server_barrier_ip:            b.server_barrier_ip || '',
      server_barrier_port:          numField(b.server_barrier_port),
      dashboard_control_enabled:    !!b.dashboard_control_enabled,
      barrier_stop_coil_address:    numField(b.barrier_stop_coil_address),
      lock_open_until_closed:       !!b.lock_open_until_closed,
      open_reminder_after_minutes:  numField(b.open_reminder_after_minutes),
      open_reminder_at_closing:     !!b.open_reminder_at_closing,
      auto_close_at:                b.auto_close_at ? String(b.auto_close_at).slice(0, 5) : '',
      open_input_address:           numField(b.open_input_address),
      closed_input_address:         numField(b.closed_input_address),
    })
  }

  const startBarrierAdd = () => { setBarrierEditingId('new'); setBarrierForm({ ...EMPTY_BARRIER }) }
  const cancelBarrierEdit = () => { setBarrierEditingId(null); setBarrierForm({}) }
  const updateBarrierForm = (field, value) => setBarrierForm(f => ({ ...f, [field]: value }))

  const saveBarrier = async (stationId) => {
    if (!barrierForm.label.trim()) {
      setError('Der Name ist ein Pflichtfeld')
      return
    }
    if (!barrierForm.barrier_ip.trim() && !barrierForm.server_barrier_ip.trim()) {
      setError('Mindestens eine Adresse ist nötig — im Netzwerk (Terminal) oder über WireGuard (Server)')
      return
    }
    setError(null)
    setBarrierSaving(true)
    const isNew = barrierEditingId === 'new'
    try {
      const res = await apiFetch(
        isNew
          ? `/api/admin/stations/${stationId}/barriers`
          : `/api/admin/stations/${stationId}/barriers/${barrierEditingId}`,
        {
          method: isNew ? 'POST' : 'PATCH',
          body: JSON.stringify({
            label:                        barrierForm.label.trim(),
            barrier_ip:                   barrierForm.barrier_ip.trim(),
            barrier_port:                 Number(barrierForm.barrier_port) || 502,
            barrier_unit_id:              Number(barrierForm.barrier_unit_id) || 1,
            barrier_coil_address:         Number(barrierForm.barrier_coil_address) || 0,
            barrier_close_coil_address:  barrierForm.barrier_close_coil_address.trim() ? Number(barrierForm.barrier_close_coil_address) : null,
            barrier_auto_close_seconds:   barrierForm.barrier_auto_close_seconds.trim() ? Number(barrierForm.barrier_auto_close_seconds) : null,
            barrier_auto_close_seconds_standby: barrierForm.barrier_auto_close_seconds_standby.trim() ? Number(barrierForm.barrier_auto_close_seconds_standby) : null,
            barrier_trigger_delay_seconds: Number(barrierForm.barrier_trigger_delay_seconds) || 0,
            camera_trigger_direction:    barrierForm.camera_trigger_direction || null,
            camera_trigger_standby_only: !!barrierForm.camera_trigger_standby_only,
            camera_trigger_delay_seconds_standby: barrierForm.camera_trigger_delay_seconds_standby.trim() ? Number(barrierForm.camera_trigger_delay_seconds_standby) : null,
            barrier_signal_mode:         barrierForm.barrier_signal_mode || 'pulse',
            // barrier_hours_source is UI-only — translated here into the two mutually
            // exclusive server fields (see validateBarrierBody on the server).
            barrier_business_hours:     barrierForm.barrier_hours_source === 'custom' ? barrierForm.barrier_business_hours : null,
            barrier_hours_reference_label: barrierForm.barrier_hours_source === 'reference' ? (barrierForm.barrier_hours_reference_label || null) : null,
            camera_trigger_stays_open_after_midnight: !!barrierForm.camera_trigger_stays_open_after_midnight,
            server_barrier_ip:            barrierForm.server_barrier_ip.trim() || null,
            server_barrier_port:          numOrNull(barrierForm.server_barrier_port),
            dashboard_control_enabled:    !!barrierForm.dashboard_control_enabled && !!barrierForm.server_barrier_ip.trim(),
            barrier_stop_coil_address:    numOrNull(barrierForm.barrier_stop_coil_address),
            // Only meaningful for a barrier that stays open — sent as off otherwise, so a
            // hidden leftover value can never fire.
            lock_open_until_closed:       formStaysOpen(barrierForm) && !!barrierForm.lock_open_until_closed,
            open_reminder_after_minutes:  formStaysOpen(barrierForm) ? numOrNull(barrierForm.open_reminder_after_minutes) : null,
            open_reminder_at_closing:     formStaysOpen(barrierForm) && !!barrierForm.open_reminder_at_closing,
            auto_close_at:                formStaysOpen(barrierForm) ? (barrierForm.auto_close_at || null) : null,
            open_input_address:           numOrNull(barrierForm.open_input_address),
            closed_input_address:         numOrNull(barrierForm.closed_input_address),
          }),
        }
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        setError(err.error || 'Schranke speichern fehlgeschlagen')
        return
      }
      const saved = await res.json()
      // The save response is the bare row; which flows use the barrier comes from the
      // station list and is kept until the next reload (a rename can change it — the
      // next load corrects that).
      setStations(prev => prev.map(s => s.id !== stationId ? s : {
        ...s,
        station_barriers: isNew
          ? [...(s.station_barriers || []), { ...saved, flow_usage: [] }]
          : (s.station_barriers || []).map(b => b.id === saved.id ? { ...saved, flow_usage: b.flow_usage || [] } : b),
      }))
      cancelBarrierEdit()
      flash(isNew ? 'Schranke hinzugefügt' : 'Schranke gespeichert')
    } catch { setError('Schranke speichern fehlgeschlagen') }
    finally { setBarrierSaving(false) }
  }

  const deleteBarrier = async (stationId, barrierId, label) => {
    if (!window.confirm(`Schranke „${label}" wirklich löschen?`)) return
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/stations/${stationId}/barriers/${barrierId}`, { method: 'DELETE' })
      if (!res.ok) { setError('Schranke löschen fehlgeschlagen'); return }
      setStations(prev => prev.map(s => s.id !== stationId ? s : {
        ...s,
        station_barriers: (s.station_barriers || []).filter(b => b.id !== barrierId),
      }))
      if (barrierEditingId === barrierId) cancelBarrierEdit()
    } catch { setError('Schranke löschen fehlgeschlagen') }
  }

  // ── Delete ───────────────────────────────────────────────────────────────────

  const handleDelete = async (stationId, name) => {
    if (!window.confirm(`Station „${name}" wirklich löschen?\nAlle Mandantenzuweisungen werden entfernt.`)) return
    try {
      const res = await apiFetch(`/api/admin/stations/${stationId}`, { method: 'DELETE' })
      if (res.ok) {
        setStations(prev => prev.filter(s => s.id !== stationId))
        if (editingId === stationId) cancelEdit()
      } else {
        setError('Löschen fehlgeschlagen')
      }
    } catch { setError('Löschen fehlgeschlagen') }
  }

  // ── 2N connectivity test ─────────────────────────────────────────────────────

  const test2n = async (stationId) => {
    setTest2nState('testing')
    try {
      const res  = await apiFetch(`/api/admin/stations/${stationId}/test-2n`, { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      setTest2nState(res.ok ? { ok: true } : { error: data.error || 'Fehler' })
    } catch { setTest2nState({ error: 'Gerät nicht erreichbar' }) }
  }

  // ── Evacuation-list PIN removal ──────────────────────────────────────────────
  // Separate from the normal Speichern flow — leaving the PIN field blank there
  // means "unverändert lassen", not "entfernen", same as the 2N password field.

  const removeEvacPin = async (stationId) => {
    if (!window.confirm('Evakuierungslisten-PIN wirklich entfernen? Der Druck-Button am Terminal wird dadurch deaktiviert.')) return
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/stations/${stationId}/evac-pin`, { method: 'DELETE' })
      if (!res.ok) { setError('PIN entfernen fehlgeschlagen'); return }
      setStations(prev => prev.map(s => s.id !== stationId ? s : { ...s, has_evac_pin: false }))
      flash('PIN entfernt')
    } catch { setError('PIN entfernen fehlgeschlagen') }
  }

  // ── Tenant assignment helpers ─────────────────────────────────────────────────

  const addTenantToEdit = () => {
    if (!addTenantId) return
    if (editTenants.some(t => t.tenant_id === addTenantId)) return
    const tenant = allTenants.find(t => t.id === addTenantId)
    setEditTenants(prev => [...prev, {
      tenant_id:          addTenantId,
      sort_order:         prev.length,
      allowed_flow_types: null,
      default_flow_type:  null,
      _tenant:            tenant,
    }])
    setAddTenantId('')
  }

  const removeTenantFromEdit = (tenantId) =>
    setEditTenants(prev => prev.filter(t => t.tenant_id !== tenantId))

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) return <div className="loading-state"><span className="spinner" /></div>

  const unassignedTenants = allTenants.filter(t => !editTenants.some(et => et.tenant_id === t.id))

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Stationen</h1>
          <p>Physische Kiosk-Stationen, 2N Audio Kit und Schranken-Konfiguration</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowCreate(true); setError(null) }}>
          + Station hinzufügen
        </button>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ {success}</div>}

      {/* ── Create form ──────────────────────────────────────────────── */}
      {showCreate && (
        <div className="card station-create-card">
          <div className="section-title">Neue Station</div>
          <div className="station-form-grid">
            <div className="form-group">
              <label>Station-ID <span className="field-required">*</span></label>
              <input className="form-control" placeholder="z.B. kiosk-empfang-01"
                value={createForm.station_id}
                onChange={e => setCreateForm(f => ({ ...f, station_id: e.target.value }))} />
              <span className="form-hint">Muss mit VITE_STATION_ID im Gerät übereinstimmen</span>
            </div>
            <div className="form-group">
              <label>Name <span className="field-required">*</span></label>
              <input className="form-control" placeholder="z.B. Empfang Hauptgebäude"
                value={createForm.name}
                onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Zugehöriger Mandant</label>
              <select className="form-control" value={createForm.owner_tenant_id}
                onChange={e => setCreateForm(f => ({ ...f, owner_tenant_id: e.target.value }))}>
                <option value="">— keiner —</option>
                {allTenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>

          <div className="station-section-header">2N Audio Kit (optional)</div>
          <div className="station-form-grid">
            <div className="form-group">
              <label>Geräte-IP / Hostname</label>
              <input className="form-control" placeholder="192.168.1.50"
                value={createForm.sip_2n_ip}
                onChange={e => setCreateForm(f => ({ ...f, sip_2n_ip: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>API-Benutzer</label>
              <input className="form-control" placeholder="admin"
                value={createForm.sip_2n_api_user}
                onChange={e => setCreateForm(f => ({ ...f, sip_2n_api_user: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>API-Passwort</label>
              <input type="password" className="form-control" placeholder="Passwort"
                value={createForm.sip_2n_api_password}
                onChange={e => setCreateForm(f => ({ ...f, sip_2n_api_password: e.target.value }))} />
            </div>
          </div>

          <div className="station-section-header">Evakuierungsliste (optional)</div>
          <div className="station-form-grid">
            <div className="form-group">
              <label>PIN</label>
              <input type="password" className="form-control" placeholder="PIN setzen"
                value={createForm.evac_pin}
                onChange={e => setCreateForm(f => ({ ...f, evac_pin: e.target.value }))} />
              <span className="form-hint">Schaltet den Druck-Button für die Evakuierungsliste am Terminal frei. Leer = Funktion deaktiviert.</span>
            </div>
          </div>
          <p className="form-hint">Schranken können nach dem Erstellen im Bearbeiten-Modus hinzugefügt werden.</p>

          <div className="row" style={{ gap: '8px', marginTop: '16px' }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
              {creating ? <span className="spinner-sm" /> : 'Erstellen'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setShowCreate(false); setCreateForm(EMPTY_CREATE) }}>
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* ── Station list ─────────────────────────────────────────────── */}
      {stations.length === 0 && !showCreate
        ? <div className="empty-state"><p>Noch keine Stationen angelegt.</p></div>
        : stations.map(s => (
          <div key={s.id} className={`card station-card${editingId === s.id ? ' station-card--editing' : ''}`}>

            {editingId !== s.id ? (
              /* ── View mode ─────────────────────────────────────────── */
              <div className="station-row">
                <div className="station-row-left">
                  <div className="station-name">{s.name}</div>
                  <div className="station-meta">
                    <code className="station-id-badge">{s.station_id}</code>
                    {s.sip_2n_ip
                      ? <span className="badge badge-green">2N konfiguriert</span>
                      : <span className="badge badge-grey">Kein 2N</span>
                    }
                    {(s.station_barriers || []).length > 0 &&
                      <span className="badge badge-green">🚧 {s.station_barriers.length} Schranke{s.station_barriers.length > 1 ? 'n' : ''}</span>
                    }
                    {(s.station_tenants || []).length > 0
                      ? (s.station_tenants || []).map(st => (
                          <span key={st.tenant_id} className="badge badge-blue">
                            {st.tenants?.name || st.tenant_id}
                          </span>
                        ))
                      : <span className="station-no-tenants">Keine Mandanten zugewiesen</span>
                    }
                  </div>
                </div>
                <div className="station-row-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => startEdit(s)}>Bearbeiten</button>
                  <button className="btn btn-ghost btn-sm station-delete-btn"
                    onClick={() => handleDelete(s.id, s.name)}>Löschen</button>
                </div>
              </div>
            ) : (
              /* ── Edit mode ─────────────────────────────────────────── */
              <div className="station-edit">
                <div className="section-title">
                  <code className="station-id-badge">{s.station_id}</code>
                  <span className="station-edit-label">{editBasic.name || s.name}</span>
                </div>

                {/* Basic info */}
                <div className="station-form-grid">
                  <div className="form-group">
                    <label>Name</label>
                    <input className="form-control" value={editBasic.name}
                      onChange={e => setEditBasic(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Zugehöriger Mandant</label>
                    <select className="form-control" value={editBasic.owner_tenant_id}
                      onChange={e => setEditBasic(f => ({ ...f, owner_tenant_id: e.target.value }))}>
                      <option value="">— keiner —</option>
                      {allTenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* 2N device config */}
                <div className="station-section-header">2N Audio Kit</div>
                <div className="station-form-grid">
                  <div className="form-group">
                    <label>Geräte-IP / Hostname</label>
                    <input className="form-control" placeholder="192.168.1.50"
                      value={editBasic.sip_2n_ip}
                      onChange={e => setEditBasic(f => ({ ...f, sip_2n_ip: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>API-Benutzer</label>
                    <input className="form-control" placeholder="admin"
                      value={editBasic.sip_2n_api_user}
                      onChange={e => setEditBasic(f => ({ ...f, sip_2n_api_user: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>
                      API-Passwort{' '}
                      {s.has_2n_password && <span className="badge badge-green">gesetzt</span>}
                    </label>
                    <input type="password" className="form-control"
                      placeholder={s.has_2n_password ? '(unverändert lassen)' : 'Passwort setzen'}
                      value={editBasic.sip_2n_api_password}
                      onChange={e => setEditBasic(f => ({ ...f, sip_2n_api_password: e.target.value }))} />
                  </div>
                </div>
                <div className="station-2n-test">
                  <button className="btn btn-ghost btn-sm"
                    onClick={() => test2n(s.id)}
                    disabled={test2nState === 'testing' || !editBasic.sip_2n_ip}>
                    {test2nState === 'testing'
                      ? <><span className="spinner-sm" style={{ marginRight: '6px' }} />Verbinde…</>
                      : 'Verbindung testen'}
                  </button>
                  {test2nState && test2nState !== 'testing' && (
                    test2nState.ok
                      ? <span className="badge badge-green">✓ Erreichbar</span>
                      : <span className="badge badge-red">{test2nState.error}</span>
                  )}
                </div>

                {/* Evacuation-list PIN — gates the kiosk's staff-triggered print
                    button (idle screen only). Not set = feature off for this station. */}
                <div className="station-section-header">Evakuierungsliste</div>
                <div className="station-form-grid">
                  <div className="form-group">
                    <label>
                      PIN{' '}
                      {s.has_evac_pin && <span className="badge badge-green">gesetzt</span>}
                    </label>
                    <div className="row" style={{ gap: '8px' }}>
                      <input type="password" className="form-control"
                        placeholder={s.has_evac_pin ? '(unverändert lassen)' : 'PIN setzen'}
                        value={editBasic.evac_pin}
                        onChange={e => setEditBasic(f => ({ ...f, evac_pin: e.target.value }))} />
                      {s.has_evac_pin && (
                        <button type="button" className="btn btn-ghost btn-sm station-delete-btn"
                          onClick={() => removeEvacPin(s.id)}>
                          Entfernen
                        </button>
                      )}
                    </div>
                    <span className="form-hint">Schaltet den Druck-Button für die Evakuierungsliste am Terminal frei. „Entfernen" deaktiviert die Funktion wieder für diese Station.</span>
                  </div>
                </div>

                {/* Barriers / Modbus config — a station may have several (e.g. Einfahrt /
                    Ausfahrt). Each one is its own independent Add/Edit/Delete now, saved
                    immediately, not bundled into the station's Speichern button below —
                    see the barrier CRUD handlers above for why. */}
                <div className="station-section-header">Schranken</div>
                {(s.station_barriers || []).length === 0 && barrierEditingId !== 'new'
                  ? <p className="station-empty-hint">Noch keine Schranke hinterlegt.</p>
                  : null
                }
                {(s.station_barriers || []).map(b => (
                  <div key={b.id} className="station-tenant-block">
                    <div className="station-tenant-header">
                      <span className="station-tenant-name">{b.label}</span>
                      {barrierEditingId !== b.id && (
                        <div className="station-tenant-actions">
                          <button className="btn btn-ghost btn-sm" onClick={() => startBarrierEdit(b)}>
                            Bearbeiten
                          </button>
                          <button className="btn btn-ghost btn-sm station-delete-btn"
                            onClick={() => deleteBarrier(s.id, b.id, b.label)}>
                            Löschen
                          </button>
                        </div>
                      )}
                    </div>
                    {barrierEditingId === b.id ? (
                      <>
                        <BarrierFormFields form={barrierForm} onChange={updateBarrierForm}
                          siblingLabels={(s.station_barriers || []).filter(x => x.id !== b.id).map(x => x.label)} />
                        <div className="station-barrier-actions">
                          <button className="btn btn-primary btn-sm" onClick={() => saveBarrier(s.id)} disabled={barrierSaving}>
                            {barrierSaving ? <span className="spinner-sm" /> : 'Speichern'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={cancelBarrierEdit}>Abbrechen</button>
                        </div>
                      </>
                    ) : (
                      <div className="station-barrier-summary">
                        <div className="station-barrier-summary-line">
                          <span className={barrierMode(b).className}>{barrierMode(b).label}</span>
                          <span className="station-empty-hint">
                            Unit {b.barrier_unit_id} · Coil {b.barrier_coil_address}
                            {b.barrier_close_coil_address != null && ` / Zu ${b.barrier_close_coil_address}`}
                            {b.barrier_stop_coil_address != null && ` / Stopp ${b.barrier_stop_coil_address}`}
                          </span>
                        </div>
                        <span className="station-empty-hint">
                          Terminal: {b.barrier_ip ? `${b.barrier_ip}:${b.barrier_port}` : '—'}
                          {' · '}Server: {b.server_barrier_ip ? `${b.server_barrier_ip}:${b.server_barrier_port ?? 502}` : 'wie Terminal'}
                        </span>
                        {(b.flow_usage || []).length > 0 && (
                          <span className="station-empty-hint">
                            Öffnet beim Check-in: {b.flow_usage.map(u => `${u.tenantName} · ${u.flowType}`).join(', ')}
                          </span>
                        )}
                        {b.server_barrier_ip && (
                          <div className="station-2n-test">
                            <button className="btn btn-ghost btn-sm"
                              disabled={barrierTests[b.id] === 'running'}
                              onClick={() => testBarrier(s.id, b.id)}>
                              {barrierTests[b.id] === 'running' ? <span className="spinner-sm" /> : 'Verbindung testen'}
                            </button>
                            {barrierTests[b.id] && barrierTests[b.id] !== 'running' && (
                              <span className={barrierTests[b.id].ok ? 'station-test-ok' : 'station-test-fail'}>
                                {barrierTests[b.id].ok
                                  ? `✓ ADAM antwortet über WireGuard (${barrierTests[b.id].ms} ms) — Relais „Auf" ist ${barrierTests[b.id].relayOn ? 'an' : 'aus'}`
                                  : `✗ ${barrierTests[b.id].error}`}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {barrierEditingId === 'new' ? (
                  <div className="station-tenant-block">
                    <div className="station-tenant-header">
                      <span className="station-tenant-name">Neue Schranke</span>
                    </div>
                    <BarrierFormFields form={barrierForm} onChange={updateBarrierForm}
                      siblingLabels={(s.station_barriers || []).map(x => x.label)} />
                    <div className="station-barrier-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => saveBarrier(s.id)} disabled={barrierSaving}>
                        {barrierSaving ? <span className="spinner-sm" /> : 'Hinzufügen'}
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={cancelBarrierEdit}>Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <button className="btn btn-ghost btn-sm station-add-contact-btn" onClick={startBarrierAdd}>
                    + Schranke hinzufügen
                  </button>
                )}

                {/* Tenant assignments */}
                <div className="station-section-header">Zugewiesene Mandanten</div>

                {editTenants.length === 0
                  ? <p className="station-empty-hint">Noch kein Mandant zugewiesen.</p>
                  : editTenants.map(et => (
                    <div key={et.tenant_id} className="station-tenant-block">
                      <div className="station-tenant-header">
                        <span className="station-tenant-name">
                          {et._tenant?.name || et.tenant_id}
                        </span>
                        <button className="btn btn-ghost btn-sm station-delete-btn"
                          onClick={() => removeTenantFromEdit(et.tenant_id)}>
                          Entfernen
                        </button>
                      </div>

                      {/* Contacts belong to the tenant since migration 038, not to a
                          station — every station of a tenant shows the same ones. */}
                      <p className="station-empty-hint">
                        Rufziele werden unter „Telefonkontakte“ gepflegt und gelten für alle Stationen dieses Mandanten.
                      </p>
                    </div>
                  ))
                }

                {/* Add tenant picker */}
                {unassignedTenants.length > 0 && (
                  <div className="station-add-tenant">
                    <select className="form-control form-control--sm" value={addTenantId}
                      onChange={e => setAddTenantId(e.target.value)}>
                      <option value="">Mandant auswählen…</option>
                      {unassignedTenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <button className="btn btn-ghost btn-sm" onClick={addTenantToEdit}
                      disabled={!addTenantId}>
                      Zuweisen
                    </button>
                  </div>
                )}

                {/* Save / Cancel / Delete */}
                <div className="station-edit-footer">
                  <button className="btn btn-primary" onClick={() => saveEdit(s.id)} disabled={saving}>
                    {saving ? <span className="spinner-sm" /> : 'Speichern'}
                  </button>
                  <button className="btn btn-ghost" onClick={cancelEdit}>Abbrechen</button>
                  <button className="btn btn-ghost btn-sm station-delete-btn station-delete-station"
                    onClick={() => handleDelete(s.id, s.name)}>
                    Station löschen
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      }

      {/* Spans every station's barriers, not just one being edited — this
          page has no single selected station, and restore already requires
          owner regardless (station_barriers has no tenant_id at all). */}
      <VersionHistoryPanel table="station_barriers" global onRestored={load} />
    </div>
  )
}

export default Stations
